import { LatLng, SingleShipmentDataExtended } from '@/apis/types';
import { weightKgToLb } from '../shellShipmentHelpers';
import theme from '@/assets/theme';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getUserTimeZone } from '@/common/interceptor/interceptor';
import { getTimeZoneAbbreviation } from '@/common/utils/getFormattedTimeUtils';
import {
  getShipmentLocationParsedAddress,
  parseShipmentDayjs,
} from '@/common/utils/utils';

dayjs.extend(utc);
dayjs.extend(timezone);
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { useShipmentPermission } from '@/hooks/useShipmentPermission';
import { themeT } from '@/utils/themeText';
import { Col, Divider, Flex, message, Row, Tag, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import arrowLink from '../../../assets/images/arrowLink.png';
import {
  getPermissions,
  truncateByLength,
  useToken,
} from '../../../common/helper';
import { CustomShipmentText, CustomTitle } from '../../../components';
import { GoogleMapsProvider } from '../../../components/GoogleMapsProvider';
import ShipmentMap from '../ShipmentMap';
import ShipmentEmissionsSection from './ShipmentEmissionsSection';
import ShipmentLegsList from './ShipmentLegsList';
import {
  LinkedShipmentCard,
  ShipmentVitals_CT,
  ViabilityWindowTag,
} from '../partials';
import {
  getLayoutFlags,
  getProductDetailSensors,
  getSensorTableRows,
  getSensorVitalsLayout,
} from '../partials/sensorVitalsLayout';
import {
  buildStatusPillStyle,
  getDeliveryStatusPillColors,
} from '../statusTagColors';
import LinkUnknownLocation from './LinkUnknownLocation';
import ProductsTableSection from './ProductsTableSection';
import SensorsTableSection from './SensorsTableSection';
import { useSensorProductLinksGate } from '@/hooks/useSensorProductLinksGate';
import ShipmentPackagesSection from './ShipmentPackagesSection';
import { useShipmentPermissions } from '../ControlTower/hooks/useShipmentPermissions';
import { ICONS_BY_STATUS } from '../GxPReportGeneration/components/GxpReportTitleSection';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux-toolkit/store';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

interface ShipmentDetailNavEntry {
  id: string;
  legType: 'CL' | 'EL';
  reference?: string;
}

interface ShipmentDetailProps {
  shipment: SingleShipmentDataExtended;
  lastPoint?: LatLng;
  filterByLinkCode: (linkCode: string) => void;
  setShipmentId: (shipmentId: string) => void;
  onLinkedShipmentClick?: (shipmentId: string) => void;
  /** Breadcrumb stack of panels opened to reach the current one (oldest
   *  first) — appended to when a leg card here opens another panel, so the
   *  back arrow can unwind the full chain (CL -> EL -> CL -> ...). */
  navStack?: ShipmentDetailNavEntry[];
  toggleDrawer?: (
    action?: string | boolean,
    type?: string,
    id?: string,
    alert?: unknown,
    legType?: 'CL' | 'EL'
  ) => void;
}

/**
 * Component that displays detailed information about a shipment including
 * delivery status, locations, products, and transport provider details.
 *
 * @param shipment - The shipment data to display
 * @param lastPoint - The last known coordinates of the shipment
 * @param filterByLinkCode - Callback to filter shipments by link code
 * @param setShipmentId - Callback to set the active shipment ID
 */
const ShipmentDetail: React.FC<ShipmentDetailProps> = ({
  shipment,
  lastPoint,
  filterByLinkCode,
  setShipmentId,
  onLinkedShipmentClick,
  navStack,
  toggleDrawer,
}) => {
  const { t } = useTranslation('shipments');
  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const canCreateShipmentForSupport = useShipmentPermission();
  const { hasOperationalLegView } = useShipmentPermissions();
  const userTimeZone = getUserTimeZone();
  const { formatDateTime } = useUserDateTimeFormat();
  const isClientTheme = theme.themeType === 2;
  const transportProviderLabel = t(
    isClientTheme ? 'carrier' : 'transportProvider',
    {
      defaultValue: isClientTheme ? 'Carrier' : 'Transport provider',
    }
  );
  const transportProviderEtaLabel = t('transportProviderEta', {
    provider: transportProviderLabel,
    defaultValue: `${transportProviderLabel} ETA`,
  });
  const shipmentReferenceLabel =
    shipment?.leg_type === 'EL'
      ? 'MBL'
      : themeT('referenceId', theme.themeType);
  const shipmentReferenceValue =
    shipment?.leg_type === 'EL' ? shipment?.mbl : shipment?.reference_number;

  const formatDateWithTZ = (date?: string | null) => {
    if (!date) return '--';
    const tz = getTimeZoneAbbreviation(date, userTimeZone);
    return `${date} ${tz ? `${tz}` : ''}`;
  };

  // "15 Aug, 2026 3:15 PM PDT". Used for shell shipment pickup date.
  // Backend sends these already in the user's configured timezone.
  const formatLocalDateTime = (date?: string | null) => {
    if (!date) return '--';
    try {
      const trimmed = date.trim();
      const hasTimezone =
        /[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed);
      const naive = parseShipmentDayjs(trimmed);
      const parsed = hasTimezone
        ? dayjs(trimmed).tz(userTimeZone)
        : naive
          ? dayjs.tz(naive.format('YYYY-MM-DDTHH:mm:ss'), userTimeZone)
          : dayjs.tz(trimmed, userTimeZone);
      const tz = getTimeZoneAbbreviation(date, userTimeZone);
      const formatted = formatDateTime(parsed, { includeTime: true });
      return `${formatted}${tz ? ` ${tz}` : ''}`;
    } catch {
      return String(date).split('T')[0];
    }
  };

  const { layoutCase, vitalCount } = getSensorVitalsLayout(shipment);
  const { showVitalsAboveHeader, showVitalsInline, viabilityInHeader } =
    getLayoutFlags(layoutCase);
  const { sensorDisplay, sensorIdDisplay, calibrationDateDisplay } =
    getProductDetailSensors(shipment);
  const linksEnabled = useSensorProductLinksGate();
  const sensorTableRows = linksEnabled ? getSensorTableRows(shipment) : [];
  const showSensorTable = sensorTableRows.length > 0;
  const deliveryStatusColors = getDeliveryStatusPillColors(
    shipment?.delivery_status
  );
  const deliveryStatus = String(shipment?.delivery_status || '').toLowerCase();
  const isDelivered =
    String(shipment?.type) === '3' || deliveryStatus.includes('delivered');
  const etaOrDeliveredLabel = isDelivered ? 'Delivered on' : 'Zoomlogi ETA';
  const etaOrDeliveredValue = isDelivered
    ? formatDateWithTZ(
        shipment?.actual_delivery_date || shipment?.estimated_delivery_date
      )
    : formatDateWithTZ(shipment?.estimated_delivery_date);

  const originName = String(shipment?.from_location?.name || '').trim();
  const originStreet = [
    shipment?.from_location?.parsed_address?.AddressNumber,
    shipment?.from_location?.parsed_address?.StreetName,
    shipment?.from_location?.parsed_address?.StreetNamePostType,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  const originCityStateCountry = getShipmentLocationParsedAddress(
    shipment?.from_location
  );
  const hasOriginInfo = Boolean(
    originName || originStreet || String(originCityStateCountry || '').trim()
  );

  const destinationName = String(shipment?.to_location?.name || '').trim();
  const destinationStreet = [
    shipment?.to_location?.parsed_address?.AddressNumber,
    shipment?.to_location?.parsed_address?.StreetName,
    shipment?.to_location?.parsed_address?.StreetNamePostType,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  const destinationCityStateCountry = getShipmentLocationParsedAddress(
    shipment?.to_location
  );
  const hasDestinationInfo = Boolean(
    destinationName ||
    destinationStreet ||
    String(destinationCityStateCountry || '').trim()
  );

  const main_menu = useSelector((state: RootState) => state.menu.items);
  const GxP_permissions = getPermissions(main_menu, 'Settings', 'GxP reports');
  const showGxpStatus = Boolean(
    GxP_permissions?.is_view &&
    Boolean(decodedToken?.temperature_gxp_enabled) &&
    shipment?.temperature_gxp?.status
  );
  // Match Origin/Destination (span 11) when GxP is hidden so Zoomlogi ETA
  // lines up with Destination. With GxP shown, keep three equal thirds.
  const statusColSpan = showGxpStatus ? 8 : 11;

  return (
    <>
      <Row justify='space-between' style={{ marginTop: '-20px' }}>
        {/* ETA Status badge  */}
        <Col span={statusColSpan}>
          <CustomShipmentText
            text={'Delivery status'}
            fontWeight={600}
            lineHeight='20px'
            marginBottom='4px'
          />
          <Tag
            style={buildStatusPillStyle(deliveryStatusColors, {
              borderRadius: '8px',
              fontSize: '10px',
            })}
          >
            {shipment?.delivery_status}
          </Tag>
        </Col>
        {/* GXP status badge  */}
        {showGxpStatus ? (
          <Col span={8}>
            <CustomShipmentText
              text={'GxP status'}
              fontWeight={600}
              lineHeight='20px'
              marginBottom='4px'
            />

            <span
              className={`gxp-status-tag gxp-status-tag--${shipment?.temperature_gxp?.status?.toLocaleLowerCase()}`}
            >
              {
                ICONS_BY_STATUS[
                  shipment?.temperature_gxp?.status?.toLocaleLowerCase() ??
                    'other'
                ]
              }
              {shipment?.temperature_gxp?.status_label}
            </span>
          </Col>
        ) : null}
        {/* ETA Date  */}
        <Col span={statusColSpan}>
          <CustomShipmentText
            text={etaOrDeliveredLabel}
            fontWeight={600}
            lineHeight='20px'
            marginBottom='4px'
          />

          <CustomShipmentText
            text={etaOrDeliveredValue}
            lineHeight='16px'
            color='var(--text-light)'
          />
        </Col>
      </Row>
      {/* map  */}
      <div className=''>
        <div className='map-container is-detail-shipment-map is-detail-shipment-map-h'>
          <GoogleMapsProvider>
            <ShipmentMap
              currentView='detail'
              lastCoordinates={lastPoint}
              shipment={shipment}
              height={'240px'}
              isLargeScreen={false}
              onPointClicked={() => {}}
            />
          </GoogleMapsProvider>
        </div>
      </div>

      <Flex vertical={true} gap={16}>
        {/* Sensor vitals: above header for single_many/multi; inline for inline layout */}
        {shipment?.can_view_vitals && showVitalsAboveHeader && (
          <Flex vertical gap={12}>
            <div>
              <ShipmentVitals_CT
                shipment={shipment}
                openCharts={true}
                layoutCase={layoutCase}
                trailing={
                  layoutCase === 'single_many' ? (
                    <ViabilityWindowTag shipment={shipment} />
                  ) : undefined
                }
              />
            </div>
            {layoutCase === 'multi' && (
              <Divider style={{ margin: '4px 0', borderColor: '#E5E5E5' }} />
            )}
          </Flex>
        )}

        {/* Shipment details header - vitals inline for 'inline' layout (case 2: same row) */}
        <Flex
          className='shipment-details-header-row'
          justify='space-between'
          align='center'
          gap={6}
          style={{
            flexWrap: showVitalsInline ? 'nowrap' : 'wrap',
          }}
        >
          <CustomTitle
            text={'Shipment details'}
            fontSize={15}
            fontWeight={700}
            lineHeight='24px'
            color='var(--text-primary)'
            marginBottom='0'
            style={
              showVitalsInline
                ? { flexShrink: 0 }
                : { width: '200px', maxWidth: '100%' }
            }
          />

          {shipment?.can_view_vitals &&
            (showVitalsInline ? (
              <Flex
                className={`shipment-details-inline-right ${
                  vitalCount >= 6 ? 'shipment-details-inline-right--wrap' : ''
                }`}
                align='center'
                gap={8}
              >
                <ShipmentVitals_CT
                  shipment={shipment}
                  openCharts={true}
                  layoutCase={layoutCase}
                />
                {viabilityInHeader && (
                  <div
                    className={`shipment-inline-viability ${
                      vitalCount >= 6
                        ? 'shipment-inline-viability--force-wrap'
                        : ''
                    }`}
                  >
                    <ViabilityWindowTag shipment={shipment} />
                  </div>
                )}
              </Flex>
            ) : (
              <Flex
                className='shipment-details-header-vitals'
                align='center'
                gap={8}
              >
                {viabilityInHeader && (
                  <ViabilityWindowTag shipment={shipment} />
                )}
              </Flex>
            ))}
        </Flex>

        <Row justify='space-between'>
          {hasOriginInfo && (
            <Col span={11}>
              <CustomShipmentText
                text={'Origin'}
                fontWeight={600}
                marginBottom='4px'
              />

              {shipment?.from_location?.name === 'Unknown Location' &&
              canCreateShipmentForSupport ? (
                <LinkUnknownLocation
                  locationId={shipment?.from_location?.id}
                  pathName={`/settings/location?openDrawer=true&isUnknown=true&id=${shipment?.from_location?.id}`}
                  locationName={shipment?.from_location?.name || ''}
                />
              ) : (
                <CustomShipmentText
                  text={originName}
                  color='var(--text-light)'
                  marginBottom='4px'
                />
              )}
              <CustomShipmentText
                text={originStreet}
                color='var(--text-light)'
                marginTop={
                  shipment?.from_location?.name === 'Unknown Location'
                    ? '4px '
                    : '0'
                }
                marginBottom='4px'
              />
              <CustomShipmentText
                text={originCityStateCountry}
                color='var(--text-light)'
              />
            </Col>
          )}

          {hasDestinationInfo && (
            <Col span={11}>
              <CustomShipmentText
                text={'Destination'}
                fontWeight={600}
                lineHeight='20px'
                marginBottom='4px'
              />

              {shipment?.to_location?.name === 'Unknown Location' &&
              canCreateShipmentForSupport ? (
                <LinkUnknownLocation
                  locationId={shipment?.to_location?.id}
                  pathName={`/settings/location?openDrawer=true&id=${shipment?.to_location?.id}`}
                  locationName={shipment?.to_location?.name || ''}
                />
              ) : (
                <CustomShipmentText
                  text={destinationName}
                  color='var(--text-light)'
                  marginBottom='4px'
                />
              )}
              <CustomShipmentText
                text={destinationStreet}
                color='var(--text-light)'
                marginTop={
                  shipment?.to_location?.name === 'Unknown Location'
                    ? '4px '
                    : '0'
                }
                marginBottom='4px'
              />
              <CustomShipmentText
                text={destinationCityStateCountry}
                color='var(--text-light)'
              />
            </Col>
          )}
        </Row>

        <Row justify='space-between'>
          <Col span={11}>
            <CustomShipmentText
              text={'Scheduled pickup'}
              fontWeight={600}
              marginBottom='4px'
            />
            <CustomShipmentText
              text={
                shipment?.is_shell && !shipment?.scheduled_pickup_date
                  ? formatLocalDateTime(
                      shipment?.customer_preferred_pickup_date
                    )
                  : formatDateWithTZ(shipment?.scheduled_pickup_date)
              }
              color='var(--text-light)'
            />
          </Col>

          <Col span={11}>
            <CustomShipmentText
              text={'Scheduled delivery'}
              fontWeight={600}
              marginBottom='4px'
            />

            <CustomShipmentText
              text={formatDateWithTZ(shipment?.scheduled_delivery_date)}
              color='var(--text-light)'
            />
          </Col>
        </Row>

        <Row justify='space-between'>
          <Col span={11}>
            <CustomShipmentText
              text={'Picked up'}
              fontWeight={600}
              marginBottom='4px'
            />
            <CustomShipmentText
              text={formatDateWithTZ(shipment?.actual_pickup_date)}
              color='var(--text-light)'
            />
          </Col>

          <Col span={11}>
            <CustomShipmentText
              text={'Delivered'}
              fontWeight={600}
              marginBottom='4px'
            />
            <CustomShipmentText
              text={formatDateWithTZ(shipment?.actual_delivery_date)}
              color='var(--text-light)'
            />
          </Col>
        </Row>

        <Row justify='space-between'>
          <Col span={11}>
            <CustomShipmentText
              text={'Total weight (kg / lbs)'}
              fontWeight={600}
              marginBottom='4px'
            />

            <CustomShipmentText
              text={
                shipment?.weight_kg
                  ? `${shipment.weight_kg} / ${weightKgToLb(shipment.weight_kg)}`
                  : ''
              }
              color='var(--text-light)'
            />
          </Col>

          <Col span={11}>
            <CustomShipmentText
              text={'Dimensions'}
              fontWeight={600}
              marginBottom='4px'
            />

            <CustomShipmentText
              text={shipment?.volume ? `${shipment.volume} Inch` : ''}
              color='var(--text-light)'
            />
          </Col>
        </Row>

        <Row justify='space-between'>
          <Col span={11}>
            <CustomShipmentText
              text={shipmentReferenceLabel}
              fontWeight={600}
              marginBottom='4px'
            />
            <TruncateWithTooltip
              text={shipmentReferenceValue}
              max={30}
              render={(t) => (
                <CustomShipmentText text={t} color='var(--text-light)' />
              )}
            />
          </Col>
          <Col span={11}>
            {hasOperationalLegView ? (
              <>
                <CustomShipmentText
                  text={'Controlling office'}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <CustomShipmentText
                  text={shipment?.controlling_office || '--'}
                  color='var(--text-light)'
                />
              </>
            ) : (
              <>
                <CustomShipmentText
                  text={themeT('linkCode', theme.themeType)}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <TruncateWithTooltip
                  text={shipment?.link_code}
                  max={30}
                  render={(t) => (
                    <span>
                      {' '}
                      <CustomShipmentText text={t} color='var(--text-light)' />
                    </span>
                  )}
                />
              </>
            )}
          </Col>
        </Row>

        {shipment?.carbon_emissions && (
          <Row justify='space-between'>
            <Col span={11}>
              <ShipmentEmissionsSection emissions={shipment.carbon_emissions} />
            </Col>
          </Row>
        )}

        {hasOperationalLegView &&
          Array.isArray(shipment?.legs_records) &&
          shipment.legs_records.length > 0 && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <ShipmentLegsList
                shipment={shipment}
                onLegClick={(leg) => {
                  const targetId =
                    shipment?.leg_type === 'CL' ? leg.id : leg.shipment_id;
                  if (!targetId) {
                    message.error(
                      shipment?.leg_type === 'CL'
                        ? 'Leg ID is not available.'
                        : 'Shipment ID is not available for this leg.'
                    );
                    return;
                  }

                  const nextNavStack: ShipmentDetailNavEntry[] = [
                    ...(navStack ?? []),
                    {
                      id: shipment.id,
                      legType: shipment.leg_type as 'CL' | 'EL',
                      reference:
                        shipment.leg_type === 'CL'
                          ? shipment.reference_number
                          : shipment.mbl,
                    },
                  ];

                  toggleDrawer?.(
                    'open',
                    'Detail',
                    targetId,
                    { navStack: nextNavStack },
                    shipment.leg_type === 'CL' ? 'EL' : 'CL'
                  );
                }}
                onSeeAll={() => {
                  toggleDrawer?.(
                    'open',
                    'shipmentLegsList',
                    shipment.id,
                    undefined,
                    shipment.leg_type === 'CL' ? 'EL' : 'CL'
                  );
                }}
              />
            </>
          )}

        {/* Linked shipments START  */}

        {shipment?.linked_shipments?.length > 0 && (
          <LinkedShipmentCard
            onLinkedShipmentClick={onLinkedShipmentClick}
            shipment={shipment}
            filterByLinkCode={filterByLinkCode}
            setShipmentId={setShipmentId}
          />
        )}

        {/* Linked shipments END */}
        {/* Products table section START  */}

        <Divider style={{ margin: '8px 0' }} />

        <ProductsTableSection
          products={shipment?.products_preview || []}
          detailColumns={shipment?.product_detail_columns}
          productsCount={shipment?.products_count || 0}
          shipmentId={shipment?.id || ''}
          onExpand={(
            value?: string | boolean,
            name = 'shipmentProductsList'
          ) => {
            toggleDrawer?.(
              value ? 'open' : 'close',
              name,
              shipment?.id ?? null,
              undefined,
              shipment?.leg_type as 'CL' | 'EL'
            );
          }}
          legType={shipment?.leg_type}
        />

        <Divider style={{ margin: '8px 0' }} />
        {shipment?.pieces?.length ? (
          <>
            <ShipmentPackagesSection
              pieces={shipment.pieces}
              courierTitle={shipment.courier?.title}
            />
            <Divider style={{ margin: '8px 0' }} />
          </>
        ) : null}
        {shipment?.leg_type === 'CL' && (
          <>
            {/* Products table section END  */}

            {showSensorTable && <SensorsTableSection rows={sensorTableRows} />}
            {!linksEnabled && (
              <>
                <Flex justify='space-between' align='center' gap={16}>
                  <CustomTitle
                    text={'Conditions & sensor'}
                    fontSize={15}
                    fontWeight={700}
                    lineHeight='28px'
                    color='var(--text-primary)'
                    marginBottom='0'
                  />
                </Flex>

                <Row justify='space-between'>
                  {/* temperature range  */}
                  <Col span={11}>
                    {shipment?.product?.project_id &&
                    shipment.product.project_id.includes(',') ? (
                      <Tooltip
                        title={
                          <div className='multiple-content-tooltip'>
                            Temperature range overlap for all products
                          </div>
                        }
                        overlayStyle={{
                          maxWidth: '154px',
                          textAlign: 'center',
                        }}
                        placement='top'
                      >
                        <CustomShipmentText
                          text={'Temperature range (shipment)'}
                          fontWeight={600}
                          marginBottom='4px'
                        />

                        <CustomShipmentText
                          text={shipment?.product?.temperature_range}
                          color='var(--text-light)'
                        />
                      </Tooltip>
                    ) : (
                      <span>
                        <CustomShipmentText
                          text={'Temperature range'}
                          fontWeight={600}
                          marginBottom='4px'
                        />
                        <CustomShipmentText
                          text={shipment?.product?.temperature_range || '--'}
                          color='var(--text-light)'
                        />
                      </span>
                    )}
                  </Col>

                  {/* Transit viability window */}
                  <Col span={11}>
                    {shipment?.product?.project_id &&
                    shipment.product.project_id.includes(',') ? (
                      <Tooltip
                        title={
                          <div className='multiple-content-tooltip'>
                            Minimum viability window for all product types
                          </div>
                        }
                        overlayStyle={{ maxWidth: '154px' }}
                        placement='top'
                      >
                        <CustomShipmentText
                          text={'Transit viability window'}
                          fontWeight={600}
                          marginBottom='4px'
                        />

                        <CustomShipmentText
                          text={
                            shipment?.product?.viability_window
                              ? shipment?.product?.viability_window + ' hrs'
                              : '--'
                          }
                          color='var(--text-light)'
                        />
                      </Tooltip>
                    ) : (
                      <>
                        <CustomShipmentText
                          text={'Transit viability window'}
                          fontWeight={600}
                          marginBottom='4px'
                        />

                        <CustomShipmentText
                          text={
                            shipment?.product?.viability_window
                              ? shipment?.product?.viability_window + ' hrs'
                              : '--'
                          }
                          color='var(--text-light)'
                        />
                      </>
                    )}
                  </Col>
                </Row>
                <Row justify='space-between'>
                  <Col span={11}>
                    <CustomShipmentText
                      text={'Sensor'}
                      fontWeight={600}
                      marginBottom='4px'
                    />

                    <CustomShipmentText
                      text={sensorDisplay}
                      color='var(--text-light)'
                    />
                  </Col>

                  <Col span={11}>
                    <CustomShipmentText
                      text={'Sensor ID'}
                      fontWeight={600}
                      marginBottom='4px'
                    />

                    <CustomShipmentText
                      text={sensorIdDisplay}
                      color='var(--text-light)'
                    />
                  </Col>
                </Row>
                {calibrationDateDisplay && (
                  <Row justify='space-between'>
                    <Col span={11}>
                      <CustomShipmentText
                        text={'Calibration Date'}
                        fontWeight={600}
                        marginBottom='4px'
                      />

                      <CustomShipmentText
                        text={calibrationDateDisplay}
                        color='#4B4B4B'
                      />
                    </Col>
                  </Row>
                )}
                {shipment?.courier_tracking_id?.startsWith('EPTN') && (
                  <Row justify='space-between'>
                    <Col span={11}>
                      <CustomShipmentText
                        text={'Transportation Temp'}
                        fontWeight={600}
                        marginBottom='4px'
                      />
                      <CustomShipmentText
                        text={'2 C to 8 C (Refrigerated)'}
                        color='var(--text-light)'
                      />
                    </Col>
                  </Row>
                )}
                <Divider style={{ margin: '8px 0' }} />
              </>
            )}
          </>
        )}

        {/* transport provider  */}

        <Flex justify='space-between' align='center' gap={16}>
          <CustomTitle
            text={transportProviderLabel}
            fontSize={15}
            fontWeight={700}
            lineHeight='28px'
            color='var(--text-primary)'
            marginBottom='0'
          />
        </Flex>

        <Row justify='space-between'>
          <Col span={11}>
            <CustomShipmentText
              text={transportProviderLabel}
              fontWeight={600}
              marginBottom='4px'
            />

            <CustomShipmentText
              text={shipment?.courier?.title}
              color='var(--text-light)'
            />
          </Col>

          <Col span={11}>
            <CustomShipmentText
              text={'Service level'}
              fontWeight={600}
              marginBottom='4px'
            />

            <CustomShipmentText
              text={shipment?.courier_service_level || '--'}
              color='var(--text-light)'
            />
          </Col>
        </Row>

        <Row justify='space-between'>
          <Col span={11}>
            <CustomShipmentText
              text={'Tracking ID'}
              fontWeight={600}
              marginBottom='4px'
            />
            {shipment?.courier?.tracking_link ? (
              <>
                <a
                  href={shipment.courier.tracking_link}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='shipment-detail-tracking shipment-detail-tracking-link'
                >
                  <TruncateWithTooltip
                    text={shipment?.courier_tracking_id}
                    max={18}
                    render={(t: string) => <span>{t}</span>}
                  />

                  <img
                    src={arrowLink}
                    alt='arrow'
                    width={15}
                    className='shipment-detail-tracking-arrow'
                  />
                </a>
              </>
            ) : (
              <span className='shipment-detail-tracking'>
                <Tooltip title={shipment?.courier_tracking_id}>
                  <span>
                    {truncateByLength(shipment?.courier_tracking_id, 23)}
                  </span>
                </Tooltip>
              </span>
            )}
          </Col>

          <Col span={11}>
            <CustomShipmentText
              text={transportProviderEtaLabel}
              fontWeight={600}
              marginBottom='4px'
            />

            <CustomShipmentText
              text={formatDateWithTZ(shipment?.transport_provider_eta)}
              color='var(--text-light)'
            />
          </Col>
        </Row>
        <Divider style={{ margin: '8px 0' }} />
      </Flex>
    </>
  );
};
export default ShipmentDetail;
